import React, { useRef, useState } from "react";
import { View, Text, TouchableOpacity, Pressable, Platform, Dimensions } from "react-native";
import { Portal } from "react-native-paper";
import { Ionicons } from "@expo/vector-icons";
import { ADMIN_COLORS, ADMIN_FONT } from "../../config/theme/adminColors";

// Menu de ações "3 pontos" no formato da tela de Alunos.
// `actions`: [{ label, icon?, onPress, danger? }].
//
// Implementação própria (não usa o <Menu> do react-native-paper): medimos o
// botão no clique com measureInWindow e posicionamos o menu num <Portal> em
// coordenadas fixas. O <Menu> do RNP se posiciona por um loop de medição via
// requestAnimationFrame que falha de forma intermitente na web quando o anchor
// está dentro de um container de altura fixa / overflow:hidden (o ContentList do
// ClickCard) — daí o sintoma "abre quando a lista tem scroll, não abre quando
// não tem". Aqui a posição é determinística, então funciona nos dois casos.
//
// Precisa de um Portal.Host ancestral (o <Provider> do react-native-paper já
// presente no ClickCard).

const MENU_WIDTH = 208;
const SCREEN_MARGIN = 8;

const AdminActionMenu = ({ actions = [], anchorColor }) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const anchorRef = useRef(null);

  const openMenu = (e) => {
    // Evita que o clique "borbulhe" para o TouchableOpacity da linha (navegação).
    e?.stopPropagation?.();

    const node = anchorRef.current;
    if (!node || !node.measureInWindow) {
      setVisible(true);
      return;
    }

    node.measureInWindow((x, y, width, height) => {
      const { width: winW, height: winH } = Dimensions.get("window");

      // Alinha a borda direita do menu com a borda direita do botão.
      let left = x + width - MENU_WIDTH;
      left = Math.max(SCREEN_MARGIN, Math.min(left, winW - MENU_WIDTH - SCREEN_MARGIN));

      // Abre abaixo do botão; se não couber, abre acima.
      const estHeight = actions.length * 42 + 12;
      let top = y + height + 4;
      if (top + estHeight > winH - SCREEN_MARGIN) {
        top = Math.max(SCREEN_MARGIN, y - estHeight - 4);
      }

      setPos({ top, left });
      setVisible(true);
    });
  };

  return (
    <>
      <TouchableOpacity
        ref={anchorRef}
        onPress={openMenu}
        // data-no-drag (web): impede que um micro-arrasto ao clicar inicie o
        // drag HTML5 da linha (que engoliria o clique). Ver FlatListView.
        {...(Platform.OS === "web" ? { dataSet: { noDrag: "true" } } : {})}
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Ionicons name="ellipsis-vertical" size={18} color={anchorColor || ADMIN_COLORS.muted} />
      </TouchableOpacity>

      {visible ? (
        <Portal>
          {/* Backdrop transparente: fecha ao clicar fora. */}
          <Pressable
            onPress={() => setVisible(false)}
            style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
          />
          <View
            style={{
              position: "absolute",
              top: pos.top,
              left: pos.left,
              width: MENU_WIDTH,
              backgroundColor: ADMIN_COLORS.surface,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: ADMIN_COLORS.border,
              paddingVertical: 6,
              boxShadow: "0 8px 28px rgba(16,24,40,0.18)",
            }}
          >
            {actions.map((action, i) => (
              <Pressable
                key={i}
                onPress={() => {
                  setVisible(false);
                  action.onPress?.();
                }}
                style={({ hovered }) => [
                  { paddingVertical: 10, paddingHorizontal: 14, borderRadius: 8, marginHorizontal: 4 },
                  hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
                ]}
              >
                <Text
                  style={{
                    fontFamily: ADMIN_FONT,
                    fontSize: 14,
                    color: action.danger ? ADMIN_COLORS.danger : ADMIN_COLORS.ink,
                  }}
                >
                  {action.label}
                </Text>
              </Pressable>
            ))}
          </View>
        </Portal>
      ) : null}
    </>
  );
};

export default AdminActionMenu;
